import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { MultiSelect } from 'primereact/multiselect';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';

import { alertService } from '../../../../shared/services/alertService';
import { usePlanningWizard } from '../PlanningWizardContext';
import { TableGridEntry } from '../types';

/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * Step 7 — Table Grid (order-copy column config, New Appointment only). Angular
 * addOneMoreTable / removeTableGrid / OrderGridSubmit. Each committed row shows
 * its value labels + key; the trailing blank row (orderId === '') renders the
 * MultiSelect + key input + Add button. Selected values are removed from the
 * available list.
 */
const StepTableGrid = () => {
  const {
    tableValueList,
    setTableValueList,
    allTableValueList,
    addedTableGridList,
    setAddedTableGridList,
    nextPage,
    previousPage,
  } = usePlanningWizard();

  const updateRow = (ind: number, field: keyof TableGridEntry, val: any) => {
    setAddedTableGridList((prev) =>
      prev.map((row, i) => (i === ind ? { ...row, [field]: val } : row))
    );
  };

  const addOneMoreTable = (ind: number, key: string, valueArr: string[]) => {
    if (!(key && key.trim() && valueArr && valueArr.length > 0)) {
      alertService.error('Please select a table key and table value');
      return;
    }
    const lblDrp = allTableValueList.filter((o) => valueArr.includes(o.value));
    const valueLabel = lblDrp.map((mp) => mp.label);
    setAddedTableGridList((prev) => {
      const next = prev.map((row, i) =>
        i === ind ? { ...row, orderId: ind + 1, valueLabel } : row
      );
      return [...next, { key: '', value: [], valueLabel: '', orderId: '' }];
    });
    setTableValueList((prev) =>
      prev.filter((o) => !valueArr.includes(o.value))
    );
  };

  const removeTableGrid = (ind: number, obj: TableGridEntry) => {
    setAddedTableGridList((prev) => prev.filter((_, i) => i !== ind));
    const add = allTableValueList.filter((o) =>
      (obj.value || []).some((v) => v === o.value)
    );
    if (add.length > 0) setTableValueList((prev) => [...prev, ...add]);
  };

  const orderGridSubmit = () => {
    if (
      addedTableGridList.length > 1 &&
      addedTableGridList[0].key &&
      addedTableGridList[0].value &&
      addedTableGridList[0].value.length > 0
    ) {
      nextPage();
    } else {
      alertService.error('Please select atleast one');
    }
  };

  return (
    <div className="card p-4">
      <DataTable value={addedTableGridList} responsiveLayout="scroll">
        <Column
          header="Order Copy Value"
          body={(row: TableGridEntry, options) =>
            row.orderId ? (
              <div className="text-wrap">
                {Array.isArray(row.valueLabel)
                  ? row.valueLabel.join(', ')
                  : row.valueLabel}
              </div>
            ) : (
              <MultiSelect
                value={row.value}
                options={tableValueList}
                optionLabel="label"
                optionValue="value"
                placeholder="Select.."
                appendTo={document.body}
                className="w-100 dropdown-fixed"
                panelClassName="dropdown-panel-fit"
                onChange={(e) =>
                  updateRow(options.rowIndex, 'value', e.value ?? [])
                }
              />
            )
          }
        />
        <Column
          header="Order Copy Label"
          body={(row: TableGridEntry, options) =>
            row.orderId ? (
              <div className="text-wrap">{row.key}</div>
            ) : (
              <InputText
                value={row.key}
                className="form-control"
                onChange={(e) =>
                  updateRow(options.rowIndex, 'key', e.target.value)
                }
              />
            )
          }
        />
        <Column
          header="Action"
          body={(row: TableGridEntry, options) =>
            row.orderId ? (
              <span>{options.rowIndex + 1}</span>
            ) : (
              <Button
                type="button"
                icon="pi pi-plus"
                label="Add"
                className="p-button-success"
                onClick={() =>
                  addOneMoreTable(options.rowIndex, row.key, row.value)
                }
              />
            )
          }
        />
        <Column
          header="Remove"
          alignHeader="center"
          body={(row: TableGridEntry, options) =>
            row.orderId ? (
              <div className="text-center">
                <i
                  className="pi pi-times-circle text-danger cursor-pointer fs-5"
                  role="button"
                  tabIndex={0}
                  onClick={() => removeTableGrid(options.rowIndex, row)}
                />
              </div>
            ) : null
          }
        />
      </DataTable>

      <div className="row mt-4">
        <div className="col-6">
          <Button
            type="button"
            icon="pi pi-angle-left"
            label="Previous"
            className="p-button-danger"
            onClick={previousPage}
          />
        </div>
        <div className="col-6 text-end">
          <Button
            type="button"
            icon="pi pi-angle-right"
            iconPos="right"
            label="Next"
            className="p-button-success"
            onClick={orderGridSubmit}
          />
        </div>
      </div>
    </div>
  );
};

export default StepTableGrid;
